import { ArrowLeft, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeliveryForOrder } from "@/features/delivery/hooks/use-delivery-tracking";
import { type OrderStatus, useOrder, useOrderHistory } from "@/hooks/use-orders";
import { formatPrice } from "@/lib/format-price";

function formatTimestamp(value: string | null): string {
  if (!value) {
    return "—";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

function timestampFieldForStatus(status: OrderStatus): { label: string; field: string } | null {
  switch (status) {
    case "accepted":
      return { label: "orders.status.accepted", field: "acceptedAt" };
    case "in_progress":
      return { label: "orders.status.in_progress", field: "startedAt" };
    case "ready":
      return { label: "orders.status.ready", field: "readyAt" };
    case "delivered":
      return { label: "orders.status.delivered", field: "deliveredAt" };
    case "cancelled":
      return { label: "orders.status.cancelled", field: "cancelledAt" };
    case "rejected":
      return { label: "orders.status.rejected", field: "rejectedAt" };
    default:
      return null;
  }
}

export default function OrderReceiptPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const orderQuery = useOrder(id);
  const historyQuery = useOrderHistory();
  const deliveryQuery = useDeliveryForOrder(id);

  if (orderQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (orderQuery.error || !orderQuery.data) {
    return (
      <div className="space-y-3 text-center">
        <h1 className="font-semibold text-xl">
          {t("orders.notFound", { defaultValue: "Pedido no encontrado" })}
        </h1>
        <p className="text-muted-foreground text-sm">{orderQuery.error?.message ?? ""}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/account/orders">{t("orders.history.back", "Volver a Mis pedidos")}</Link>
        </Button>
      </div>
    );
  }

  const { order, items } = orderQuery.data;
  const status = order.status as OrderStatus;
  const isDelivery = order.fulfillmentType === "delivery";
  const hasDelivery = Boolean(deliveryQuery.data);
  const historyItem = historyQuery.data?.orders.find((item) => item.id === order.id);
  const organizationSlug = historyItem?.organizationSlug ?? null;
  const showTrackingLink = isDelivery && hasDelivery && Boolean(organizationSlug);
  const terminalTimestamp = timestampFieldForStatus(status);

  return (
    <div className="space-y-4">
      <div>
        <Button asChild size="sm" variant="ghost">
          <Link to="/account/orders">
            <ArrowLeft className="h-4 w-4" />
            {t("orders.history.back", "Volver a Mis pedidos")}
          </Link>
        </Button>
      </div>

      <header className="space-y-2">
        <h1 className="font-semibold text-2xl">
          {t("orders.receipt.title", { defaultValue: "Pedido" })} #{order.dailyNumber}
        </h1>
        <p className="text-muted-foreground text-sm">{historyItem?.organizationName ?? ""}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {t(`orders.status.${status}`, { defaultValue: status.replace("_", " ") })}
          </Badge>
          <Badge variant="outline">
            {t(`orders.payment.${order.paymentStatus}`, { defaultValue: order.paymentStatus })}
          </Badge>
          <Badge variant="outline">
            {t(`orders.fulfillment.${order.fulfillmentType}`, {
              defaultValue: order.fulfillmentType.replace("_", " "),
            })}
          </Badge>
        </div>
      </header>

      {showTrackingLink ? (
        <Button asChild>
          <Link to={`/m/${organizationSlug}/delivery/order/${order.id}/track`}>
            <MapPin className="h-4 w-4" />
            {t("orders.receipt.trackDelivery", "Seguir en el mapa")}
          </Link>
        </Button>
      ) : null}

      {order.rejectionReason ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <h2 className="font-semibold text-destructive text-sm">
            {t("orders.status.rejectedTitle", { defaultValue: "Pedido rechazado" })}
          </h2>
          <p className="mt-1 text-sm">{order.rejectionReason}</p>
        </div>
      ) : null}

      <section className="space-y-2 rounded-lg border border-border bg-card p-4">
        <h2 className="font-semibold text-sm uppercase tracking-wide">
          {t("orders.status.items", { defaultValue: "Items" })}
        </h2>
        <ul className="space-y-2">
          {items.map((item) => (
            <li className="flex items-center justify-between text-sm" key={item.id}>
              <span>
                {item.quantity} × {item.nameSnapshot}
              </span>
              <span className="tabular-nums">
                {formatPrice(item.priceSnapshot * item.quantity, order.currency)}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-border border-t pt-2 font-semibold">
          <span>{t("orders.status.total", { defaultValue: "Total" })}</span>
          <span className="tabular-nums">{formatPrice(order.total, order.currency)}</span>
        </div>
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("orders.receipt.details", { defaultValue: "Detalle" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <DetailRow
            label={t("orders.receipt.createdAt", { defaultValue: "Realizado" })}
            value={formatTimestamp(order.createdAt)}
          />
          {terminalTimestamp ? (
            <DetailRow
              label={t(terminalTimestamp.label, { defaultValue: terminalTimestamp.field })}
              value={formatTimestamp(
                (order as unknown as Record<string, string | null>)[terminalTimestamp.field] ??
                  null,
              )}
            />
          ) : null}
          {order.contactName ? (
            <DetailRow
              label={t("orders.receipt.contact", { defaultValue: "Contacto" })}
              value={order.contactName}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
